import {
  GetSubmissionResponse,
  useReviewExemptionPortalSubmission as reviewExemptionPortalSubmission,
} from '@commenda/api';
import { SubmissionStatusFilter } from '@commenda/api/exemption-portal/types';
import {
  Alert,
  Badge,
  BadgeProps,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DocViewerComponent,
  Icons,
  LoadingIndicator,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import { getFileRecordForSellerSubmission } from '@commenda/api/file-record';
import {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import mixpanel from '@commenda/mixpanel';
import {
  copyToClipboard,
  ExemptionPortalHelper,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import { ExemptionSubmissionStatus } from '@prisma/client';
import { State } from 'country-state-city';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { DataPoint } from '../../../../../Corporation/common/DataPoint';
import { useNotification } from '../../../../../hooks/useNotification';
import { getStateName } from '../../../../utils';
import {
  CERTIFICATE_TYPE_OPTIONS,
  EXEMPTION_ID_TYPE_NAME_MAP,
  EXEMPTION_REASON_NAME_MAP,
} from '../../constants';
import { CertificateFormData } from '../../utils/certificateSerializer';
import ApproveCertificateConfirmationModal from './ApproveCertificateConfirmationModal';
import RejectCertificateModal from './RejectCertificateModal';

type StatusConfig = {
  variant: 'success' | 'warning' | 'destructive' | 'info';
  title: string;
  description: string;
  icon: JSX.Element;
};

const STATUS_CONFIGS: Record<
  SubmissionStatusFilter,
  (rejectReason?: string, rejectDetails?: string) => StatusConfig
> = {
  [SubmissionStatusFilter.APPROVED]: () => ({
    variant: 'success',
    title: 'Certificate approved',
    description:
      'This certificate is active and valid across all covered states',
    icon: <Icons.CheckCircle className="size-5 text-text-success" />,
  }),
  [SubmissionStatusFilter.UNDER_REVIEW]: () => ({
    variant: 'warning',
    title: 'Review this exemption certificate',
    description:
      'Verify the exemption details and approve or reject this certificate',
    icon: <Icons.AlertTriangle className="size-5 text-text-warning" />,
  }),
  [SubmissionStatusFilter.REJECTED]: (rejectReason, rejectDetails) => ({
    variant: 'destructive',
    title: 'Certificate rejected',
    description: `This certificate was rejected. Reason: "${rejectReason || 'No reason provided'}"${rejectDetails ? `\n${rejectDetails}` : ''}`,
    icon: <Icons.AlertCircle className="size-5 text-text-error" />,
  }),
  [SubmissionStatusFilter.EXPIRING_SOON]: () => ({
    variant: 'warning',
    title: 'Certificate expiring soon',
    description:
      'This certificate is approaching its expiration date',
    icon: <Icons.AlertTriangle className="size-5 text-text-warning" />,
  }),
  [SubmissionStatusFilter.EXPIRED]: () => ({
    variant: 'destructive',
    title: 'Certificate expired',
    description:
      'This certificate has expired and is no longer valid for tax exemption',
    icon: <Icons.AlertCircle className="size-5 text-text-error" />,
  }),
};

const SUBMISSION_STATUS_BADGE_VARIANTS: Record<
  ExemptionSubmissionStatus,
  BadgeProps['variant']
> = {
  [ExemptionSubmissionStatus.APPROVED]: 'success',
  [ExemptionSubmissionStatus.UNDER_REVIEW]: 'warning',
  [ExemptionSubmissionStatus.REJECTED]: 'error',
};

interface ViewCertificateModalProps {
  isShown: boolean;
  certificate: GetSubmissionResponse;
  onClose: () => void;
  onUpdate: () => void;
}

const ViewCertificateModal = ({
  isShown,
  certificate,
  onClose,
  onUpdate,
}: ViewCertificateModalProps) => {
  const rejectDialogVisibility = useToggle();
  const approveDialogVisibility = useToggle();
  const isCertificateRejectLoading = useToggle();
  const isCertificateApproveLoading = useToggle();
  const certificateFormData =
    certificate.formData as unknown as CertificateFormData;

  const router = useRouter();

  const { setNotification } = useNotification();

  const statusConfig = STATUS_CONFIGS[certificate.derivedStatus]?.(
    certificate.rejectionReason,
    (certificate?.rejectionDetails as any)?.note || undefined
  ) || {
    variant: 'info' as const,
    title: 'Unknown status',
    description: 'The status of this certificate is unknown.',
    icon: <Icons.AlertCircle className="size-5 text-text-secondary" />,
  };

  let stateCodes: string[] = [];
  let stateNames: string[] = [];

  if (certificateFormData?.jurisdictions) {
    // New format with jurisdictions array
    stateCodes = certificateFormData.jurisdictions.map((j: any) => j.state);
    stateNames = stateCodes.map(
      (code: string) => State.getStateByCodeAndCountry(code, 'US')?.name || '-'
    );
  }

  const certificateType = certificateFormData?.type || '';
  const certificateTypeDisplay = CERTIFICATE_TYPE_OPTIONS.find(
    (option) => option.value.toString() === certificateType?.toString()
  )?.label;

  const effectiveDate = certificateFormData?.effective_date || '';
  const effectiveDateDisplay = effectiveDate
    ? NonTimezoneSensitiveDate.fromNativeDate(new Date(effectiveDate)).format(
      'MMM D, YYYY'
    )
    : '-';

  const expirationDate = certificate.expiresAt || null;
  const expirationDateDisplay = expirationDate
    ? NonTimezoneSensitiveDate.fromNativeDate(new Date(expirationDate)).format(
      'MMM D, YYYY'
    )
    : "Doesn't expire";

  const exemptionReason = certificateFormData?.reason || '';
  const exemptionReasonDisplay = exemptionReason
    ? EXEMPTION_REASON_NAME_MAP[exemptionReason] || '-'
    : '-';

  const derivedStatus =
    (certificate?.derivedStatus as SubmissionStatusFilter) || null;
  const derivedStatusDisplay = derivedStatus
    ? ExemptionPortalHelper.DERIVED_STATUS_NAME_MAP[derivedStatus] || '-'
    : '-';

  const statusCode = certificate?.status as SubmissionStatusFilter;
  const statusDisplay = statusCode
    ? ExemptionPortalHelper.DERIVED_STATUS_NAME_MAP[statusCode] || '-'
    : '-';

  const certificateHeader = `Verify ${certificateTypeDisplay} certificate - ${exemptionReasonDisplay} exemption`;

  const [signedCertificateFileUrl, setSignedCertificateFileUrl] = useState<
    string | null
  >(null);

  const getSignedUrlForGet = async () => {
    const fileRecord = await getFileRecordForSellerSubmission(
      certificate.customerSellerRelationship.corporationId,
      certificate.id
    );

    if (fileRecord.success) {
      setSignedCertificateFileUrl(fileRecord.data?.signedUrl || null);
    }
  };

  useEffect(() => {
    if (certificate.certificateFileUrl) {
      getSignedUrlForGet();
    }
  }, [certificate.certificateFileUrl]);

  const onRejectCertificate = async (reason: string, note: string) => {
    isCertificateRejectLoading.show();
    const response = await reviewExemptionPortalSubmission(
      certificate.id,
      {
        approved: false,
        rejectionReason: reason,
        rejectionDetails: { note },
      },
      certificate.customerSellerRelationship?.corporationId
    );

    if (onUpdate) onUpdate();

    if (response.success) {
      mixpanel.track(MIXPANEL_EVENTS.CERTIFICATE_ACTION_TAKEN, {
        surface_area: MIXPANEL_SURFACE_AREA.INDIRECT_TAX_APP,
        action: 'reject',
      });
      setNotification({
        title: 'Certificate rejected',
        message: 'The certificate has been rejected',
        show: true,
        valid: true,
      });
      onClose();
    } else {
      setNotification({
        title: 'Failed to reject certificate',
        message: response.error,
        show: true,
        valid: false,
      });
    }
    isCertificateRejectLoading.hide();
  };

  const onApproveCertificate = async () => {
    isCertificateApproveLoading.show();
    const response = await reviewExemptionPortalSubmission(
      certificate.id,
      {
        approved: true,
      },
      certificate.customerSellerRelationship?.corporationId
    );

    if (onUpdate) onUpdate();

    if (response.success) {
      mixpanel.track(MIXPANEL_EVENTS.CERTIFICATE_ACTION_TAKEN, {
        surface_area: MIXPANEL_SURFACE_AREA.INDIRECT_TAX_APP,
        action: 'accept',
      });
      setNotification({
        title: 'Certificate approved',
        message: 'The certificate has been approved',
        show: true,
        valid: true,
      });
      onClose();
    } else {
      setNotification({
        title: 'Failed to approve certificate',
        message: response.error,
        show: true,
        valid: false,
      });
    }
    isCertificateApproveLoading.hide();
  };

  const sortedJurisdictions = certificateFormData.jurisdictions?.sort(
    (a, b) => {
      const stateA = getStateName(a.state);
      const stateB = getStateName(b.state);
      return stateA.localeCompare(stateB);
    }
  );

  const columnGroups = [
    {
      id: 'state',
      columns: [
        {
          accessorKey: 'state',
          header: 'State',
          cell: ({ row }) => {
            const state = row.getValue('state');
            return <div>{getStateName(state)}</div>;
          },
        },
      ],
    },
    {
      id: 'identification_type',
      columns: [
        {
          accessorKey: 'identification_type',
          header: 'Identification type',
          cell: ({ row }) => {
            const identificationType = row.getValue('identification_type');
            const identificationTypeDisplay =
              EXEMPTION_ID_TYPE_NAME_MAP[identificationType];
            return <div>{identificationTypeDisplay}</div>;
          },
        },
      ],
    },
    {
      id: 'identification_number',
      columns: [
        {
          accessorKey: 'identification_number',
          header: 'Identification number',
          cell: ({ row }) => {
            const identificationNumber = row.getValue('identification_number');
            return identificationNumber ? (
              <div>{identificationNumber}</div>
            ) : (
              <Badge variant="warning" size="sm">
                Missing
              </Badge>
            );
          },
        },
      ],
    },
  ];

  return (
    <>
      <Dialog open={isShown} onOpenChange={onClose}>
        <DialogContent className="w-[1200px] max-w-[95vw] flex flex-col">
          <DialogHeader className="flex items-start flex-col">
            <div className="flex items-center gap-md">
              <DialogTitle>{certificateHeader}</DialogTitle>
              <div className="text-text-tertiary txt-small-regular flex items-center gap-lg">
                <span className="size-1.5 bg-text-quaternary rounded-full" />
                <span className="mt-0.5">
                  {certificate.customerSellerRelationship?.customerName}
                </span>
              </div>
              <Badge
                variant={
                  SUBMISSION_STATUS_BADGE_VARIANTS[
                  certificate.status as SubmissionStatusFilter
                  ]
                }
                size="sm"
              >
                {
                  ExemptionPortalHelper.DERIVED_STATUS_NAME_MAP[
                  certificate.status as SubmissionStatusFilter
                  ]
                }
              </Badge>
            </div>
          </DialogHeader>

          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex flex-col overflow-hidden h-full">
              <Alert
                variant={statusConfig.variant}
                title={statusConfig.title}
                description={statusConfig.description}
                icon={statusConfig.icon}
                _className="rounded-none border-x-0 border-t-0"
              />
              <div className="flex flex-col gap-6 p-3xl overflow-y-auto flex-1">
                <div>
                  <h2 className="txt-large-bold mb-4">Certificate details</h2>
                  <div className="border border-border-primary rounded-2xl overflow-hidden">
                    <DataPoint
                      title="Seller name"
                      value={certificate.customerSellerRelationship?.sellerName}
                      className="border-b"
                      onCopy={() =>
                        copyToClipboard(
                          certificate.customerSellerRelationship?.sellerName
                        )
                      }
                    />
                    <DataPoint
                      title="Customer name"
                      value={
                        certificate.customerSellerRelationship?.customerName
                      }
                      className="border-b"
                      onCopy={() =>
                        copyToClipboard(
                          certificate.customerSellerRelationship?.customerName
                        )
                      }
                    />
                    <DataPoint
                      title="Certificate type"
                      value={certificateTypeDisplay}
                      className="border-b"
                      onCopy={() => copyToClipboard(certificateTypeDisplay)}
                    />
                    <DataPoint
                      title="States"
                      value={
                        <div className="flex gap-2">
                          {stateNames.slice(0, 2).map((stateName, index) => (
                            <Badge key={stateName} variant="gray" size="sm">
                              {stateName}
                            </Badge>
                          ))}
                          {stateNames.length > 2 && (
                            <Tooltip
                              content={stateNames.slice(2).join(', ')}
                              side="right"
                            >
                              <Badge variant="gray" size="sm">
                                +{stateNames.length - 2} more
                              </Badge>
                            </Tooltip>
                          )}
                        </div>
                      }
                      className="border-b"
                      onCopy={() => copyToClipboard(stateNames.join(', '))}
                    />
                    <DataPoint
                      title="Reason for exemption"
                      value={exemptionReasonDisplay}
                      className="border-b"
                      onCopy={() => copyToClipboard(exemptionReasonDisplay)}
                    />
                    <DataPoint
                      title="Effective start date"
                      value={effectiveDateDisplay}
                      className="border-b"
                      onCopy={() => copyToClipboard(effectiveDateDisplay)}
                    />
                    <DataPoint
                      title="Expiration date"
                      value={expirationDateDisplay}
                      onCopy={() => copyToClipboard(expirationDateDisplay)}
                    />
                  </div>

                  <div className="mt-4">
                    <h2 className="txt-large-bold mb-4">Jurisdictions</h2>
                    <DataTable
                      data={sortedJurisdictions || []}
                      columnGroups={columnGroups}
                    />
                  </div>
                </div>

                <div>
                  {certificate.certificateFileUrl ? (
                    <div className="flex flex-col gap-2 h-[400px] bg-bg-primary overflow-y-auto">
                      {signedCertificateFileUrl ? (
                        <DocViewerComponent uri={signedCertificateFileUrl} />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <LoadingIndicator />
                        </div>
                      )}
                    </div>
                  ) : (
                    <Alert
                      variant="info"
                      title="No certificate file"
                      description="This certificate has no file attached to it."
                      icon={
                        <Icons.AlertCircle className="size-5 text-text-info" />
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="border-t border-border-primary">
              <div className="flex gap-3 items-center justify-between w-full">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => {
                    router.push(
                      `/sales-tax/exemption-certificate/${certificate.id}?corporationId=${certificate.customerSellerRelationship?.corporationId}&customerId=${certificate.customerSellerRelationship?.externalCustomerId}`
                    );
                  }}
                >
                  View certificate
                </Button>

                <div className="flex items-center gap-lg">
                  {certificate.derivedStatus ===
                    SubmissionStatusFilter.UNDER_REVIEW && (
                      <>
                        <Button
                          variant="destructive-secondary"
                          size="md"
                          onClick={rejectDialogVisibility.show}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="primary"
                          size="md"
                          onClick={approveDialogVisibility.show}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  {certificate.derivedStatus ===
                    SubmissionStatusFilter.REJECTED && (
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={approveDialogVisibility.show}
                      >
                        Approve certificate
                      </Button>
                    )}
                  {certificate.derivedStatus ===
                    SubmissionStatusFilter.APPROVED && (
                      <Button
                        variant="destructive-secondary"
                        size="md"
                        onClick={rejectDialogVisibility.show}
                      >
                        Reject certificate
                      </Button>
                    )}
                </div>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <RejectCertificateModal
        isOpen={rejectDialogVisibility.visible}
        onClose={rejectDialogVisibility.hide}
        onConfirm={onRejectCertificate}
        isLoading={isCertificateRejectLoading.visible}
      />

      <ApproveCertificateConfirmationModal
        isOpen={approveDialogVisibility.visible}
        onClose={approveDialogVisibility.hide}
        onConfirm={onApproveCertificate}
        isRejectedCertificate={
          certificate.derivedStatus === SubmissionStatusFilter.REJECTED
        }
        isLoading={isCertificateApproveLoading.visible}
      />
    </>
  );
};

export default ViewCertificateModal;
